import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { FaPlus, FaTrash, FaDownload } from 'react-icons/fa';
import Layout from '../../hocs/Layout';
import { connect } from 'react-redux';
import { resourceService } from '../../services/resourceService';
import { getFileIcon } from '../../utils/fileUtils';
import ResourceUpload from '../../components/modals/ResourceUpload';
import { toast } from 'react-toastify';

function ResourceManager({ isAuthenticated, user }) {
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showUpload, setShowUpload] = useState(false);

    const loadResources = async () => {
        try {
            const data = await resourceService.getResources();
            setResources(data.filter(res => res.category !== 'release_notes'));
        } catch (error) {
            toast.error('Error al cargar recursos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadResources();
    }, []);

    const handleDelete = async (id, title) => {
        if (window.confirm(`¿Eliminar "${title}"?`)) {
            try {
                await resourceService.deleteResource(id);
                toast.success('Recurso eliminado');
                loadResources();
            } catch (error) {
                toast.error('Error al eliminar recurso');
            }
        }
    };

    const getCategoryBadge = (category) => {
        const variants = {
            electricidad: 'success',
            gas: 'primary',
            legal: 'warning',
            otros: 'secondary',
            release_notes: 'info'
        };
        return <Badge bg={variants[category] || 'secondary'}>{category}</Badge>;
    };

    const getAccessBadge = (access) => {
        const variants = {
            all: 'secondary',
            internal: 'info',
            external: 'dark'
        };
        const labels = {
            all: 'Todos',
            internal: 'Interno',
            external: 'Externo'
        };
        return <Badge bg={variants[access] || 'secondary'}>{labels[access] || access}</Badge>;
    };

    if (loading) {
        return (
            <Layout lg={10} md={12}>
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Cargando recursos...</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <Container className="py-4">
                <Row className="mb-4">
                    <Col>
                        <h2 className="text-primary fw-bold">Gestión de Recursos</h2>
                        <p className="text-muted">Administra los archivos disponibles para descarga</p>
                    </Col>
                    <Col xs="auto">
                        <Button variant="primary" onClick={() => setShowUpload(true)}>
                            <FaPlus className="me-2" />
                            Subir Recurso
                        </Button>
                    </Col>
                </Row>

                <Card className="shadow-sm border-0">
                    <Card.Body>
                        <Table responsive hover>
                            <thead className="bg-light">
                                <tr>
                                    <th>Título</th>
                                    <th>Categoría</th>
                                    <th>Acceso</th>
                                    <th>Subido por</th>
                                    <th>Fecha</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {resources.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-4 text-muted">No hay recursos subidos</td>
                                    </tr>
                                ) : (
                                    resources.map(resource => {
                                        const { icon: FileIcon, color: iconColor } = getFileIcon(resource.file);
                                        return (
                                            <tr key={resource.id}>
                                                <td className="align-middle">
                                                    <FileIcon className={`${iconColor} me-2`} />
                                                    {resource.title}
                                                </td>
                                                <td className="align-middle">{getCategoryBadge(resource.category)}</td>
                                                <td className="align-middle">{getAccessBadge(resource.accessible_by)}</td>
                                                <td className="align-middle">{resource.uploaded_by_name || 'Admin'}</td>
                                                <td className="align-middle">{new Date(resource.upload_date).toLocaleDateString()}</td>
                                                <td className="align-middle">
                                                    <a
                                                        href={resource.file_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-sm btn-outline-primary me-2 rounded-circle"
                                                        title="Descargar"
                                                    >
                                                        <FaDownload />
                                                    </a>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        className="rounded-circle"
                                                        onClick={() => handleDelete(resource.id, resource.title)}
                                                        title="Eliminar"
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>

                <ResourceUpload
                    show={showUpload}
                    onHide={() => setShowUpload(false)}
                    onSuccess={loadResources}
                />
            </Container>
        </Layout>
    );
}

const mapStateToProps = (state) => ({
    isAuthenticated: state.Auth.isAuthenticated,
    user: state.Auth.user
});

export default connect(mapStateToProps)(ResourceManager);
